import React from "react";
import {
    FiMonitor,
    FiSmartphone,
    FiTablet,
    FiPrinter,
    FiCpu,
    FiTv,
    FiPackage,
    FiHardDrive,
    FiMove,
    FiSquare,
    FiMousePointer,
    FiGrid,
    FiPhone,
} from "react-icons/fi";

/**
 * [polecenie właściciela 2026-09-20] JEDEN KATALOG IKON TYPÓW SPRZĘTU.
 *
 * ── CO BYŁO ŹLE (ZMIERZONE 2026-09-20, nie założone) ─────────────────────────────────────────
 * Nazwa ikony typu urządzenia (`ItamDeviceTypes.Icon`) była tłumaczona na komponent w SIEDMIU
 * miejscach, każde z własną, inną kopią mapy:
 *   DeviceTypesManager.js (ICON_OPTIONS, 7 pozycji)      CreateDeviceModal.js (ICON_MAP, 7)
 *   EditDeviceModal.js (ICON_MAP, 7)                     MyNewTicketModal.js (ICON_MAP, 7 + 3 dyskowe)
 *   DevicesPage.js (ICON_MAP, 6 — BEZ FiTv)              DeviceExpandedRow.js (ICON_MAP, 6 — BEZ FiTv)
 *   MyDeptEquipmentPage.js (ICON_MAP, 6 — BEZ FiTv)
 * Skutek był na produkcji: typ „Telewizor” ma w bazie `Icon = "FiTv"`, więc w słowniku i w oknie
 * zgłoszenia pokazywał telewizor, a na liście sprzętu, karcie sprzętu i wyposażeniu działu —
 * PACZKĘ, tak samo jak „Inne”, „Kabel DP” i „Kabel HDMI”. Nic nie rzucało błędu: brak klucza
 * w mapie po cichu schodzi do ikony domyślnej. Ta sama klasa wady wywróciła drzewo HR 19.09
 * (sześć nazw ikon spoza zestawu, dziewięć kafelków wyglądających identycznie).
 *
 * ── REGUŁA ───────────────────────────────────────────────────────────────────────────────────
 * Ikona typu sprzętu ma DOKŁADNIE JEDNO źródło: ten plik. Dodanie ikony = dopisanie pozycji
 * TUTAJ; wszystkie ekrany dostają ją tym samym ruchem. Zapadki: backend
 * `Bj1TypySprzetuIWykupTests` (ikona każdego typu z seedera musi stać w tym pliku i być tu
 * zaimportowana z react-icons/fi) oraz front
 * `ikonyTypowSprzetu.test.js` (każda pozycja renderuje INNY <svg> niż domyślny, a żaden ekran
 * nie ma własnej kopii mapy).
 *
 * ⚠ Zestaw ikon to `react-icons/fi` (Feather). Feather NIE MA ikony myszy ani klawiatury —
 * stąd `FiMousePointer` (kursor) dla myszy i `FiGrid` (siatka klawiszy) dla klawiatury.
 * Nazwa spoza tego pliku (albo spoza Feather) nie wywala się, tylko renderuje paczkę — dlatego
 * zapadka sprawdza ISTNIENIE nazwy, a nie to, że kod się skompilował.
 */

/** Nazwa ikony używanej, gdy typ nie ma ikony albo ma nazwę spoza katalogu. */
export const IKONA_DOMYSLNA = "FiPackage";

/**
 * Katalog ikon dostępnych dla typu sprzętu. `value` to napis zapisywany w bazie
 * (`ItamDeviceTypes.Icon`), `label` — etykieta w liście wyboru w słowniku typów.
 */
export const IKONY_TYPOW_SPRZETU = [
    { value: "FiMonitor", label: "Monitor / laptop", Icon: FiMonitor },
    { value: "FiCpu", label: "Komputer", Icon: FiCpu },
    { value: "FiSmartphone", label: "Telefon komórkowy", Icon: FiSmartphone },
    { value: "FiPhone", label: "Telefon stacjonarny", Icon: FiPhone },
    { value: "FiTablet", label: "Tablet", Icon: FiTablet },
    { value: "FiPrinter", label: "Drukarka", Icon: FiPrinter },
    { value: "FiTv", label: "Telewizor", Icon: FiTv },
    { value: "FiHardDrive", label: "Stacja dokująca", Icon: FiHardDrive },
    { value: "FiMove", label: "Ramię / uchwyt", Icon: FiMove },
    { value: "FiMousePointer", label: "Mysz", Icon: FiMousePointer },
    { value: "FiGrid", label: "Klawiatura", Icon: FiGrid },
    { value: "FiSquare", label: "Podkładka / mata", Icon: FiSquare },
    { value: "FiPackage", label: "Inne / akcesoria", Icon: FiPackage },
];

/** Mapa „nazwa z bazy → komponent”. */
export const MAPA_IKON_SPRZETU = Object.fromEntries(
    IKONY_TYPOW_SPRZETU.map((i) => [i.value, i.Icon]),
);

/**
 * Podpowiedź ikony po NAZWIE typu — dla wierszy ewidencji sprzed słownika typów, które mają
 * wyłącznie napis `deviceType` i żadnego `deviceTypeEntity`.
 *
 * 🔴 KOLEJNOŚĆ TO CAŁA REGUŁA. „Telefon stacjonarny” zawiera „telefon”, więc dopasowanie do
 * komórki musi stać PO nim; „podkładka pod mysz” zawiera „mysz”, więc podkładka musi stać PRZED
 * myszą. Odwrotna kolejność nie wywala się — po prostu rysuje złą ikonę.
 */
const PODPOWIEDZI_PO_NAZWIE = [
    [["stacja dokuj", "stacja dok", "dokuj", "dock"], "FiHardDrive"],
    [["ramie do monitora", "ramię do monitora", "ramie monitora", "ramię monitora", "uchwyt"], "FiMove"],
    [["podkladk", "podkładk", "mata pod"], "FiSquare"],
    [["mysz", "myszka", "mouse"], "FiMousePointer"],
    [["klawiatur", "keyboard"], "FiGrid"],
    [["telefon stacjonarn", "stacjonarny telefon", "aparat telefoniczny"], "FiPhone"],
    [["laptop", "notebook"], "FiMonitor"],
    [["telefon", "phone", "komork", "komórk"], "FiSmartphone"],
    [["tablet"], "FiTablet"],
    [["drukark", "print", "skaner"], "FiPrinter"],
    [["telewizor", "monitor tv"], "FiTv"],
    [["monitor", "ekran"], "FiMonitor"],
    [["komputer", "stacjonarn", "desktop"], "FiCpu"],
];

const bezOgonkow = (tekst) =>
    String(tekst || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/ł/g, "l");

/** Nazwa ikony dla typu: najpierw zapis z bazy, potem podpowiedź po nazwie, na końcu domyślna. */
export const nazwaIkonyTypuSprzetu = (ikonaZBazy, nazwaTypu) => {
    if (ikonaZBazy && MAPA_IKON_SPRZETU[ikonaZBazy]) return ikonaZBazy;

    const nazwa = bezOgonkow(nazwaTypu);
    if (nazwa) {
        for (const [frazy, ikona] of PODPOWIEDZI_PO_NAZWIE) {
            if (frazy.some((f) => nazwa.includes(bezOgonkow(f)))) return ikona;
        }
    }
    return IKONA_DOMYSLNA;
};

/** Komponent ikony dla typu (do użycia z własnym `size` na każdym ekranie). */
export const ikonaTypuSprzetu = (ikonaZBazy, nazwaTypu) =>
    MAPA_IKON_SPRZETU[nazwaIkonyTypuSprzetu(ikonaZBazy, nazwaTypu)];

/**
 * Gotowa ikona urządzenia z ewidencji. Przyjmuje wiersz sprzętu w postaci, w jakiej oddają go
 * endpointy ITAM (`deviceTypeEntity.icon` + `deviceType` jako napis) albo sam typ ze słownika.
 */
export const IkonaTypuSprzetu = ({ ikona, typ, size }) => {
    const Ikona = ikonaTypuSprzetu(ikona, typ);
    // Brak `size` zostawiamy react-icons (1em) — karta sprzętu rysowała ikonę bez rozmiaru
    // i wymuszenie tu 16 px zmieniłoby jej wygląd przy okazji porządków w katalogu ikon.
    return size === undefined ? <Ikona /> : <Ikona size={size} />;
};

export const IkonaUrzadzenia = ({ urzadzenie, size }) => (
    <IkonaTypuSprzetu
        ikona={urzadzenie?.deviceTypeEntity?.icon || urzadzenie?.typIkona || urzadzenie?.icon}
        typ={urzadzenie?.deviceTypeEntity?.name || urzadzenie?.deviceType || urzadzenie?.typNazwa || urzadzenie?.name}
        size={size}
    />
);

export default IKONY_TYPOW_SPRZETU;
